// src/screens/energy-reset/CourseScreen.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE } from "../../utils/api";
import { auth } from "../../api/auth";

import { getSnapshot, findEnrollment } from "../../api/snapshot";
import { getCourses } from "../../api/courses";
import { getCourseSettingsByCourseId } from "../../utils/energy-reset/settings";

type Block = {
  id: number;
  block_type: string;
  order: number;
  audience: string[];
  title: string;
  content: string;
  links?: { label: string; url: string; color?: string }[];
};

const getCurrentAudience = (): string => "marketplace";

const BlockRenderer: React.FC<{ block: Block }> = ({
  block,
}) => (
  <section className="mb-8 p-6 bg-white rounded-xl shadow">
    {block.title && <h2 className="text-xl font-bold mb-2">{block.title}</h2>}
    {block.content && (
      <div
        className="mb-4"
        dangerouslySetInnerHTML={{ __html: block.content }}
      />
    )}
    {block.links && block.links.length > 0 && (
      <div className="flex gap-3">
        {block.links.map((link, i) => {
          return (
<a
              key={i}
              href={link.url}
              style={{
                background: link.color || "#FFE066",
                color: "#333",
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: 700,
                display: "inline-block",
                boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
                transition: "background 0.2s",
              }}
              className="hover:opacity-90 focus:ring-2 focus:ring-black"
              target="_blank"
              rel="noopener noreferrer"
            >
              {link.label}
            </a>
          );
        })}
      </div>
    )}
  </section>
);

const CourseScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const courseId = Number(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [blocks, setBlocks] = useState<Block[]>([]);
  const [loading, setLoading] = useState(true);
  const [courseDesign, setCourseDesign] = useState<{
    course_background_color: string | null;
    dashboard_background_image_url: string | null;
  } | null>(null);
  // ERRSTATE (08.09.2026, форма контракта обновлена M5-STORE4 18.09.2026):
  // true только когда пользователь уже зачислен на курс, но
  // getCourseSettingsByCourseId() вернул "unavailable" — то есть настройки
  // курса реально не удалось получить (сеть/сервер, и в IndexedDB тоже
  // пусто), не «у курса их нет» (легитимное отсутствие — "ok" с DEFAULTS
  // или "empty" — не взводит guardError).
  // Раньше null молча трактовался как durationDays=0 → finished=false →
  // слепая навигация в Dashboard без проверки, что состояние вообще известно.
  const [guardError, setGuardError] = useState(false);
  // M5-STEP2 (15.09.2026): отдельно от guardError — тот взводится только
  // когда пользователь уже зачислен, а settings не пришли. Этот флаг —
  // getSnapshot() сам не смог ответить (unavailable) или эффект упал
  // исключением (catch). "empty" сюда не попадает — это штатный "не куплено".
  const [snapshotError, setSnapshotError] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  // Guard: если у пользователя уже активен этот курс — редиректим
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!courseId) return;

        if (!auth.isAuthenticated()) {
          return;
        }

        setGuardError(false);
        setSnapshotError(false);
        const [snapResult, settingsResult] = await Promise.all([
          getSnapshot(),
          getCourseSettingsByCourseId(String(courseId)),
        ]);

        if (!alive) return;

        // M5-STEP2: три случая снапшота больше не схлопнуты в один return.
        if (snapResult.status === "unavailable") {
          console.error("[course-guard] snapshot unavailable, staying on landing");
          setSnapshotError(true);
          return;
        }
        if (snapResult.status === "empty") {
          return; // сервер ответил, данных нет — лендинг корректен
        }
        // M5-STORE1: остались "ok" и "cached" — оба несут .data, оба пускают
        // на дашборд как живой ответ (человек с купленным курсом офлайн должен
        // попасть внутрь). Отдельная ветка не нужна: узкий тип уже даёт .data.
        const snap = snapResult.data;

        const enr = findEnrollment(snap, courseId);
        if (!enr) return; // курса нет в прогрессе — остаёмся на лендинге

        // M5-STORE4: guardError только при unavailable (не смогли спросить —
        // не гадаем «завершён/не завершён», явная ошибка с повтором). "empty"
        // (404/400/403/401 — сервер определённо ответил) ведёт себя как
        // snapResult.status==="empty" выше — молча остаёмся на лендинге.
        if (settingsResult.status === "unavailable") {
          setGuardError(true);
          return;
        }
        if (settingsResult.status === "empty") {
          return;
        }
        const settings = settingsResult.data; // "ok" или "cached" — оба несут .data

        const durationDays = Number(settings.duration_days ?? 0);
        const finished =
          durationDays > 0 && (enr.current_day ?? 0) > durationDays;

        if (finished) return; // курс завершён — можно повторно активировать

        // курс активен → уводим дальше
        if (!snap.profile?.timezone || String(snap.profile.timezone).trim() === "") {
          navigate("/set-timezone");
        } else {
          navigate(`/dashboard/${courseId}`);
        }
      } catch (e) {
        // M5-STEP2: раньше молча игнорили — тот же слепой лендинг, что был
        // у unavailable до этой правки. Теперь тоже взводим флаг.
        console.error("[course-guard] exception in guard effect:", e instanceof Error ? e.message : String(e));
        setSnapshotError(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [courseId, navigate, searchParams, retryTick]);

  // Подгрузка лендинговых блоков
  useEffect(() => {
    if (!courseId) return;
    fetch(
      `${API_BASE}/items/course_landing_blocks?filter[course_id][_eq]=${courseId}&sort[]=order`
    )
      .then((res) => res.json())
      .then((data) => setBlocks(data.data || []))
      .catch(() => setBlocks([]))
      .finally(() => setLoading(false));
  }, [courseId]);

  // Загрузка дизайна курса из /api/courses
  useEffect(() => {
    if (!courseId) return;
    let alive = true;
    (async () => {
      const result = await getCourses();
      if (!alive) return;
      // "cached" — офлайн/сбой сети, дизайн берётся из последнего известного
      // каталога. Отказ ("empty"/"unavailable") оставляет дефолтный фон,
      // поведение не меняется — как раньше на любом .catch().
      if (result.status === "ok" || result.status === "cached") {
        const found = result.data.find((c) => Number(c.id) === courseId);
        setCourseDesign(found?.design ?? null);
      } else {
        setCourseDesign(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [courseId]);

  if (snapshotError)
    return (
      <div className="text-center py-12">
        <p className="mb-4 text-red-500">
          Не удалось проверить доступ к курсу. Проверьте соединение и попробуйте снова.
        </p>
        <button
          onClick={() => setRetryTick((t) => t + 1)}
          style={{
            background: "#FFE066",
            color: "#333",
            borderRadius: "12px",
            padding: "10px 24px",
            fontWeight: 700,
            display: "inline-block",
            boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
            transition: "background 0.2s",
          }}
          className="hover:opacity-90 focus:ring-2 focus:ring-black"
        >
          Попробовать снова
        </button>
      </div>
    );
  if (guardError)
    return (
      <div className="text-center py-12">
        <p className="mb-4 text-red-500">
          {/* SW-PRELOAD (19.09.2026), Шаг 5: guardError уже означает "не смогли
              спросить настройки курса, и в IndexedDB тоже пусто" (см.
              settings.ts) — но это же условие срабатывает и при живой сети,
              когда сервер реально отвечает ошибкой. Текст про непредзагруженный
              курс уместен только офлайн — при живой сети формулировка не меняется. */}
          {typeof navigator !== "undefined" && navigator.onLine === false
            ? "Курс ещё не загружен. Откройте его один раз при связи — дальше он будет работать без интернета."
            : "Не удалось загрузить курс. Проверьте соединение и попробуйте снова."}
        </p>
        <button
          onClick={() => setRetryTick((t) => t + 1)}
          style={{
            background: "#FFE066",
            color: "#333",
            borderRadius: "12px",
            padding: "10px 24px",
            fontWeight: 700,
            display: "inline-block",
            boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
            transition: "background 0.2s",
          }}
          className="hover:opacity-90 focus:ring-2 focus:ring-black"
        >
          Попробовать снова
        </button>
      </div>
    );
  if (loading) return <div className="text-center py-12">Загрузка...</div>;
  if (!blocks.length)
    return (
      <div className="text-center py-12 text-red-500">
        Лендинг для этого курса не найден.
      </div>
    );

  const currentAudience = getCurrentAudience();
  const filteredBlocks = blocks.filter(
    (block) =>
      !block.audience ||
      block.audience.includes(currentAudience) ||
      block.audience.length === 0
  );

  return (
    <div
      className="min-h-screen px-4 py-8"
      style={{
        background: courseDesign?.course_background_color || 'var(--color-course-background)'
      }}
    >
      {filteredBlocks.map((block) => (
        <BlockRenderer
          key={block.id}
          block={block}
        />
      ))}
    </div>
  );
};

export default CourseScreen;